import React, { useState, useEffect } from "react";
import axios from "axios";
import { getDistance } from "geolib";
import "./AreaSearch.css";

const AreaSearch = () => {
  const [cities, setCities] = useState([]);
  const [filteredCities, setFilteredCities] = useState([]);
  const [selectedCity, setSelectedCity] = useState("");
  const [radius, setRadius] = useState(50);
  const [allCustomers, setAllCustomers] = useState([]);
  const [nearbyCustomers, setNearbyCustomers] = useState([]);
  const [showSearchButton, setShowSearchButton] = useState(false);
  const [fetchedLocation, setFetchedLocation] = useState({
    latitude: null,
    longitude: null,
  });
  const [showResults, setShowResults] = useState(false);
  const [locationError, setLocationError] = useState(false);
  const [selectedItem, setSelectedItem] = useState("");
  const [items, setItems] = useState([]);
  const [filteredItems, setFilteredItems] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  const updateSliderStyle = (value) => {
    const percentage = ((value - 0) * 100) / (100 - 0);
    return `linear-gradient(90deg, #007bff ${percentage}%, #ddd ${
      percentage + 0.1
    }%)`;
  };

  const handleSearchCity = async () => {
    try {
      // Reset error state before making a new request
      setLocationError(false);

      const response = await axios.get(
        `${
          process.env.REACT_APP_API_URL
        }/api/location?city=${encodeURIComponent(selectedCity)}`
      );

      if (response.data && response.data.latitude && response.data.longitude) {
        // Update the fetchedLocation state and show results if successful
        setShowResults(true);
        setFetchedLocation({
          latitude: response.data.latitude,
          longitude: response.data.longitude,
        });
      } else {
        // Handle the case where no location data is returned but the request didn't technically fail
        setLocationError(true);
        setShowResults(false);
      }
    } catch (error) {
      console.error("Failed to fetch location:", error);
      // Set error state to true to inform the user, hide results
      setLocationError(true);
      setShowResults(false);

      // Optionally, handle different types of errors differently
      if (error.response && error.response.status === 404) {
        // Specific message for 404 errors
        console.log("City not found.");
      } else {
        // General error handling
        console.log("An error occurred while searching for the city.");
      }
    }
  };

  useEffect(() => {
    const slider = document.querySelector(".radius-slider");
    if (slider) {
      slider.style.background = updateSliderStyle(radius);
    }
  }, [radius]);

  useEffect(() => {
    setIsLoading(true);
    axios
      .get(`${process.env.REACT_APP_API_URL}/api/cities`)
      .then((response) => setCities(response.data));

    axios
      .get(`${process.env.REACT_APP_API_URL}/api/customers`)
      .then((response) => {
        setAllCustomers(response.data);
        setIsLoading(false);

        // Initialize a Set to collect unique items
        const allItems = new Set();

        // Process each customer
        response.data.forEach((customer) => {
          // Check if items_taking exists and is a string
          if (
            customer.items_taking &&
            typeof customer.items_taking === "string"
          ) {
            try {
              // Parse the JSON string to an array
              const itemsArray = JSON.parse(customer.items_taking);

              // Check if the parsed result is an array
              if (Array.isArray(itemsArray)) {
                itemsArray.forEach((item) => {
                  allItems.add(item);
                });
              }
            } catch (error) {
              console.error("Error parsing items_taking JSON:", error);
            }
          }
        });

        // Update state with unique items
        setItems([...allItems]);
      });
  }, []);

  useEffect(() => {
    if (!selectedCity) {
      setFilteredCities([]);
      setShowSearchButton(false);
      setLocationError(false);
      setFetchedLocation({ latitude: null, longitude: null });
      return;
    }

    // Filtering cities based on user input.
    const matchedCities = cities.filter((city) =>
      city.city.toLowerCase().includes(selectedCity.toLowerCase())
    );
    setFilteredCities(matchedCities);

    // Determine if there's an exact match for the selected city in the format "City -- State".
    const isExactMatch = cities.some(
      (city) =>
        `${city.city} -- ${city.state}`.toLowerCase() ===
        selectedCity.toLowerCase()
    );

    // Show the search button only if there's no exact match.
    setShowSearchButton(!isExactMatch);
    setShowResults(isExactMatch);

    // Debugging: Log the state of 'isExactMatch' to see if it matches expectation.
    // console.log("Is Exact Match:", isExactMatch);

    // If it's an exact match, also consider resetting any fetched location as the user has selected a known city.
    if (isExactMatch) {
      setFetchedLocation({ latitude: null, longitude: null });
    }
  }, [selectedCity, cities]);

  useEffect(() => {
    if (!selectedItem) {
      setFilteredItems([]);
      return;
    }

    const matchedItems = items.filter((item) =>
      item.toLowerCase().includes(selectedItem.toLowerCase())
    );
    setFilteredItems(matchedItems);
  }, [selectedItem, items]);

  useEffect(() => {
    // Only proceed if there is a selected city or fetchedLocation is available
    if (!selectedCity.trim() && !fetchedLocation.latitude) {
      setNearbyCustomers([]);
      return;
    }

    // Determine the source of latitude and longitude
    const sourceLat = fetchedLocation.latitude
      ? fetchedLocation.latitude
      : cities.find((city) => `${city.city} -- ${city.state}` === selectedCity)
          ?.latitude;
    const sourceLng = fetchedLocation.longitude
      ? fetchedLocation.longitude
      : cities.find((city) => `${city.city} -- ${city.state}` === selectedCity)
          ?.longitude;

    if (sourceLat && sourceLng && showResults) {
      let tempFilteredCustomers = allCustomers
        .map((customer) => {
          const distance =
            getDistance(
              { latitude: sourceLat, longitude: sourceLng },
              { latitude: customer.Latitude, longitude: customer.Longitude }
            ) / 1000;
          return { ...customer, distance };
        })
        .filter(
          (customer) =>
            customer.distance <= radius &&
            (!selectedItem ||
              customer.items_taking
                .toLowerCase()
                .includes(selectedItem.toLowerCase()))
        )
        .sort((a, b) => b.Avg_Sales - a.Avg_Sales);

      // Apply the sales thresholds if only the city is selected
      if (selectedCity && !selectedItem) {
        tempFilteredCustomers = tempFilteredCustomers.filter((customer) => {
          if (
            (customer.Company === "TRUMAC" || customer.Company === "CAMRUT") &&
            customer.Avg_Sales > 20000
          ) {
            return true;
          }
          if (customer.Company === "MEDISYNC" && customer.Avg_Sales > 15000) {
            return true;
          }
          if (
            (customer.Company === "CIALTO" || customer.Company === "PYTHUS") &&
            customer.Avg_Sales > 10000
          ) {
            return true;
          }
          return false;
        });
      }

      setNearbyCustomers(tempFilteredCustomers);
    } else {
      setNearbyCustomers([]);
    }
  }, [
    selectedCity,
    selectedItem,
    radius,
    allCustomers,
    cities,
    fetchedLocation,
    showResults,
  ]);

  return (
    <div>
      <div className="area-search-container">
        {isLoading ? (
          <div className="loading-indicator">Data is Loading...</div>
        ) : (
          <>
            <div className="search-input-container">
              <input
                type="text"
                value={selectedCity}
                onChange={(e) => setSelectedCity(e.target.value)}
                list="cities"
                placeholder="Enter city name..."
                disabled={isLoading}
              />
              {selectedCity && (
                <button
                  className="clear-search"
                  onClick={() => setSelectedCity("")}
                >
                  Clear
                </button>
              )}
              {/* Other components remain unchanged */}
            </div>
            {/* <input
          type="text"
          value={selectedCity}
          onChange={(e) => setSelectedCity(e.target.value)}
          list="cities"
          placeholder="Enter city name..."
        /> */}

            {showSearchButton && (
              <button className="search-city-button" onClick={handleSearchCity}>
                Search City
              </button>
            )}
            <datalist id="cities">
              {filteredCities.map((city, index) => (
                <option key={index} value={`${city.city} -- ${city.state}`} />
              ))}
            </datalist>
            <input
              type="text"
              value={selectedItem}
              onChange={(e) => setSelectedItem(e.target.value)}
              list="items"
              placeholder="Enter item name..."
              className="item-input"
              disabled={isLoading}
            />
            <datalist id="items">
              {filteredItems.map((item, index) => (
                <option key={index} value={item} />
              ))}
            </datalist>
            <input
              type="range"
              min="0"
              max="100"
              step="10"
              value={radius}
              className="radius-slider"
              onChange={(e) => setRadius(e.target.value)}
            />
            <span className="slider-value">{radius} km</span>
          </>
        )}
      </div>

      {locationError && (
        <div className="error-message">
          Unable to find the location. Please try a different city or check the
          city name for typos.
        </div>
      )}

      {showResults && selectedCity.trim() !== "" && (
        <div className="results-container">
          {["TRUMAC", "CAMRUT", "MEDISYNC", "CIALTO", "PYTHUS"].map(
            (companyName) => {
              const companyCustomers = nearbyCustomers.filter(
                (customer) => customer.Company === companyName
              );

              return (
                <div key={companyName} className="company-results">
                  <h2>
                    {companyName} Parties Nearby {selectedCity}{" "}
                    {selectedItem != "" ? "taking " + selectedItem : ""}
                  </h2>
                  {companyCustomers.length > 0 ? (
                    companyCustomers.map((customer, index) => (
                      <div key={index} className="customer-card">
                        <div className="party-name">{customer.Party_Name}</div>
                        <div className="separator"></div>
                        <div className="card-content">
                          <div className="card-section left">
                            <p>
                              {customer.City}, {customer.State}
                            </p>
                            <p>{customer.Employee}</p>
                            <p>
                              <strong>{customer.Company}</strong>
                            </p>
                          </div>
                          <div className="card-section right">
                            <p>
                              <strong>Avg Sale (Per Month) :</strong>{" "}
                              {customer.Avg_Sales}/-
                            </p>
                            <p>
                              <strong>Distance :</strong>{" "}
                              {customer.distance.toFixed(2)} km
                            </p>
                          </div>
                        </div>
                      </div>
                    ))
                  ) : (
                    <p>No Customers in {companyName} found for this area.</p>
                  )}
                </div>
              );
            }
          )}
        </div>
      )}
    </div>
  );
};

export default AreaSearch;
